// src/hooks/common/useTokenValidation.js
import { useState, useEffect, useCallback } from "react";
import { gql } from "@apollo/client";
import client from "src/api/apollo/apolloClient";
import {
  getCookie,
  getLocalStorage,
  setCookie,
  setLocalStorage,
  setGraphqlAuthentication,
} from "../../helpers/localStorage";

const LOGIN_DETAIL_BY_TOKEN = gql`
  query LoginDetailByToken($token: String!) {
    loginDetailByToken(token: $token) {
      token
      login {
        loginId
        accountType
        username
        status
      }
    }
  }
`;

const CLIENT_DETAIL_BY_LOGIN_ID = gql`
  query ClientDetail($loginId: Int!) {
    clientDetail(loginId: $loginId) {
      id
      clientName
      contactNumber
      contactPerson
      loginId
      partnerLoginId
    }
  }
`;

const FALLBACK_LOGIN_ID = "1544";

// ─── Helpers ────────────────────────────────────────────────────────────────

export const decodeJwtPayload = (token) => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const isJwtExpired = (payload) => {
  if (!payload?.exp) return false; // no expiry = treat as valid
  return Date.now() >= payload.exp * 1000;
};

// ─── Token states ────────────────────────────────────────────────────────────
export const TOKEN_STATE = {
  IDLE: "idle",
  LOADING: "loading",
  VALID: "valid",
  MISSING: "missing",   // no token in URL and no session
  EXPIRED: "expired",   // JWT exp claim is past
  INVALID: "invalid",   // server rejected the token
};

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Validates the token from the URL (for -v2 routes) or the existing session.
 *
 * Returns:
 *   tokenState  – one of TOKEN_STATE
 *   session     – { loginId, username, clientName, … } when VALID
 *   retry       – function to re-run validation
 */
export const useTokenValidation = ({ pathname, search, isV2Route }) => {
  const [tokenState, setTokenState] = useState(TOKEN_STATE.IDLE);
  const [session, setSession] = useState(null);

  const validate = useCallback(async () => {
    setTokenState(TOKEN_STATE.LOADING);

    const searchParams = new URLSearchParams(search || "");
    const urlToken = String(searchParams.get("token") || "").trim();
    const urlLoginId = String(searchParams.get("loginId") || "").trim();
    const cookieToken = getCookie("token");
    const existingUserId = getCookie("userId");
    const existingClientLoginId = getLocalStorage("clientLoginId");

    // ── 1. Determine the token to validate ───────────────────────────────
    const tokenToValidate = urlToken || cookieToken || "";
    setCookie("token", tokenToValidate);

    if (!tokenToValidate || !urlToken) {
      // No token anywhere
      if ((existingUserId || existingClientLoginId) && urlToken) {

        const payload = decodeJwtPayload(tokenToValidate);

        if (isJwtExpired(payload)) {
          setTokenState(TOKEN_STATE.EXPIRED);
          return;
        }
        // There is an existing session even without token — keep going
        setSession({ loginId: existingUserId || existingClientLoginId });
        setTokenState(TOKEN_STATE.VALID);
        return;
      } else {
        setTokenState(TOKEN_STATE.MISSING);
        return;
      }
    }

    // ── 2. Decode JWT and check expiry ────────────────────────────────────
    const payload = decodeJwtPayload(tokenToValidate);

    if (isJwtExpired(payload)) {
      setTokenState(TOKEN_STATE.EXPIRED);
      return;
    }

    const derivedUsername =
      payload?.username || payload?.name || payload?.email || "";

    // ── 3. Validate with the server ───────────────────────────────────────
    try {
      const loginRes = await client.query({
        query: LOGIN_DETAIL_BY_TOKEN,
        variables: { token: tokenToValidate },
        fetchPolicy: "network-only",
      });

      const loginDetailByToken = loginRes?.data?.loginDetailByToken;
      const loginFromToken = loginDetailByToken?.login;

      if (!loginDetailByToken || !loginFromToken) {
        setTokenState(TOKEN_STATE.INVALID);
        return;
      }

      const queryLoginId = Number.parseInt(urlLoginId, 10);
      const apiLoginId = Number.parseInt(loginFromToken?.loginId, 10);
      const resolvedLoginId = Number.isFinite(queryLoginId)
        ? queryLoginId
        : Number.isFinite(apiLoginId)
          ? apiLoginId
          : Number.parseInt(FALLBACK_LOGIN_ID, 10);

      // ── 4. Fetch client detail (best-effort) ──────────────────────────
      let clientDetail = null;
      if (Number.isFinite(apiLoginId)) {
        try {
          const clientRes = await client.query({
            query: CLIENT_DETAIL_BY_LOGIN_ID,
            variables: { loginId: apiLoginId },
            fetchPolicy: "network-only",
          });
          clientDetail = clientRes?.data?.clientDetail || null;
        } catch {
          // non-blocking — proceed without client detail
        }
      }

      const clientName = String(
        clientDetail?.clientName ||
        getLocalStorage("username") ||
        derivedUsername ||
        "Utilisateur"
      ).trim();

      // ── 5. Persist session ────────────────────────────────────────────
      setGraphqlAuthentication({
        token: loginDetailByToken?.token || tokenToValidate,
        refreshToken: loginDetailByToken?.refreshToken || "",
        login: {
          loginId: resolvedLoginId,
          accountType: loginFromToken?.accountType || "",
          username: loginFromToken?.username || derivedUsername || "",
          status: loginFromToken?.status,
        },
        clientName,
        contactNumber: clientDetail?.contactNumber || "",
        contactPerson: clientDetail?.contactPerson || "",
      });

      setCookie("userId", `${resolvedLoginId}`);
      setCookie("token", loginDetailByToken?.token || tokenToValidate);
      setLocalStorage("clientLoginId", `${resolvedLoginId}`);

      setSession({
        loginId: resolvedLoginId,
        username: loginFromToken?.username || derivedUsername,
        clientName,
        accountType: loginFromToken?.accountType,
      });

      setTokenState(TOKEN_STATE.VALID);
    } catch {
      // Server error — treat as invalid
      setTokenState(TOKEN_STATE.INVALID);
    }
  }, [search, pathname]);

  useEffect(() => {
    // Only validate on V2 routes or when there is no existing session
    const hasSession = !!getCookie("token") || !!getCookie("userId");
    if (isV2Route || !hasSession) {
      validate();
    } else {
      setSession({ loginId: getCookie("userId") });
      setTokenState(TOKEN_STATE.VALID);
    }
  }, [isV2Route, validate]);

  return { tokenState, session, retry: validate };
};
